import { Component, inject, OnInit, QueryList, ViewChildren } from '@angular/core';
import { ContentAreaComponent } from "src/app/components/content-area/content-area.component";
import { FormAreaComponent } from "src/app/components/form-area/form-area.component";
import { InputComponent } from "src/app/components/input/input.component";
import { MatSlideToggle } from "@angular/material/slide-toggle";
import { CardAreaComponent } from "src/app/components/card-area/card-area.component";
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IButtonType } from 'src/app/interfaces/components/button.interface';
import { TContentAreaAction } from 'src/app/interfaces/components/content-area.interface';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { EmailCardComponent } from 'src/app/components/email-card/email-card.component';
import { AddressCardComponent } from 'src/app/components/address-card/address-card.component';
import {
  contentAreaActions,
  buttonsEmployeeForm,
} from './employee-form.constants';
import { AlertService } from 'src/app/services/alert.service';
import { EmployeeService } from 'src/app/services/employee/employee.service';
import { populateFormArray } from 'src/app/utils/form-array.utils';
import { IAddress, IEmail } from 'src/app/interfaces/employee/employee.interface';

@Component({
  selector: 'app-employee-form',
  standalone: true,
  imports: [
    ContentAreaComponent,
    FormAreaComponent,
    InputComponent,
    MatSlideToggle,
    CardAreaComponent,
    EmailCardComponent,
    TranslateModule,
    ReactiveFormsModule,
    AddressCardComponent
],
  templateUrl: './employee-form.component.html'
})
export class EmployeeFormComponent implements OnInit {
  @ViewChildren(AddressCardComponent) addressCardComponents!: QueryList<AddressCardComponent>;
  @ViewChildren(EmailCardComponent) emailCardComponents!: QueryList<EmailCardComponent>;

  private readonly fb = inject(FormBuilder);
  private readonly route = inject(Router);
  private readonly router = inject(ActivatedRoute)
  private readonly dialog = inject(MatDialog);
  private readonly translate = inject(TranslateService);
  private readonly alertService = inject(AlertService);
  private readonly employeeService = inject(EmployeeService);

  buttonsEmployeeForm = buttonsEmployeeForm;
  contentAreaActions = contentAreaActions;

  id = '';
  title = '';

  createEmailForm(email?: IEmail): FormGroup {
    return this.fb.group({
      id: [email?.id ?? null],
      email: [email?.email ?? '', [Validators.required, Validators.email]],
      isMain: [email?.isMain ?? false],
    });
  }

  createAddressForm(address?: IAddress): FormGroup {
    return this.fb.group({
      id: [address?.id ?? null],
      addressType: [address?.addressType ?? '', Validators.required],
      zipCode: [address?.zipCode ?? '', Validators.required],
      state: [address?.state ?? '', Validators.required],
      city: [address?.city ?? '', Validators.required],
      street: [address?.street ?? '', Validators.required],
      neighborhood: [address?.neighborhood ?? '', Validators.required],
      number: [address?.number ?? '', Validators.required],
      complement: [address?.complement ?? ''],
    });
  }

  form: FormGroup = this.fb.group({
    id: [null],
    document: ['', Validators.required],
    name: ['', Validators.required],
    status: [true],
    emails: this.fb.array([]),
    addresses: this.fb.array([]),
  });

  ngOnInit(): void {
    this.id = this.router.snapshot.params['id'] ?? '';
    this.title = this.id ? 'EMPLOYEE_FORM.EDIT_TITLE' : 'EMPLOYEE_FORM.ADD_TITLE';

    if (this.id) {
      this.initialFormValue();
      this.form.get('id')?.setValue(this.id);
    } else {
      this.addAddress();
      this.addEmail();
    }
  }

  initialFormValue() {
    this.employeeService.getById(this.id).subscribe((res) => {
      const data = res.data;

      this.form.patchValue({
        id: data.id,
        document: data.document,
        name: data.name,
        status: data.status
      });

      populateFormArray(this.emails, data.emails, (email) =>
        this.createEmailForm(email)
      );

      populateFormArray(this.addresses, data.addresses, (address) =>
        this.createAddressForm(address)
      );
    });
  }

  handleContentAreaEvent(event: TContentAreaAction) {
    if (event === 'back') {
      this.cancelEvent();
    }
  }

  handleFormAreaEvent(event: IButtonType) {
    if (event === 'save') {
      this.submitForm();
    }
    if (event === 'cancel') {
      this.cancelEvent();
    }
  }

  back() {
    this.route.navigate(['/panel-master/employees']);
  }

  cancelEvent() {
    if (this.form.touched) {
      this.dialog.open(ModalComponent, {
        data: {
          type: 'cancel',
        },
      })
      .afterClosed().subscribe((confirm) => {
        if (confirm) {
         this.back();
        }
      });
    } else {
      this.back();
    }
  }

  validateIsMainEmail() {
    return this.emailCardComponents.some(component => {
      return component.control().get('isMain')?.value === true;
    });
  }

  submitForm() {
    this.form.markAllAsTouched();

    if (this.form.valid) {
      if (this.validateIsMainEmail() === false) {
        this.alertService.show({
          type: 'alert',
          subtitle: 'EMPLOYEE_FORM.MODAL.MAIN_EMAIL_REQUIRED'
        });
        return;
      }
      else {
      this.employeeService.register(this.form.getRawValue()).subscribe({
        next: () => {
          this.alertService.show({
            type: 'success',
            subtitle: 'USER_MASTER_FORM.MODAL.SUCCESS_MESSAGE'
          });
          this.back();
        },
        error: (error) => {
          this.alertService.show({
            type: 'error',
            subtitle: error?.error?.message || 'USER_MASTER_FORM.MODAL.ERROR_MESSAGE'
          });
          }
        });
      }
    }
    else {
      this.addressCardComponents.forEach(component => {
        component.isFormInvalid();
      });
      this.emailCardComponents.forEach(component => {
        component.isFormInvalid();
      });
      this.alertService.show({
        type: 'alert',
        subtitle: 'ALERT_MODAL.DEFAULT_MESSAGE_ALERT'
      });
    }
  }

  get addresses(): FormArray {
    return this.form.get('addresses') as FormArray;
  }

  get emails(): FormArray {
    return this.form.get('emails') as FormArray;
  }

  addAddress(): void {
    if (this.addresses.length === 0 || this.form.get('addresses')?.valid) {
      this.addresses.push(this.createAddressForm());
    } else {
      this.addresses.controls.forEach(control => {
        control.markAllAsTouched();
      });
    }
  }

  removeAddress(index: number): void {
    this.addresses.removeAt(index);
  }

  addEmail(): void {
    if (this.emails.length === 0 || this.form.get('emails')?.valid) {
      this.emails.push(this.createEmailForm());
    } else {
      this.emails.controls.forEach(control => {
        control.markAllAsTouched();
      });
    }
  }

  removeEmail(index: number): void {
    this.emails.removeAt(index);
  }
}
